/**
 * Card — Eine Lead-Karte (BOARD-08, BOARD-09, BOARD-10, ADMIN-03).
 *
 * Draggable via @dnd-kit/core useDraggable. Drag-vs-Click-Detection
 * uebernimmt der DndContext im Board (activationConstraint distance:8).
 *
 * Layout (oben -> unten):
 *  0. Checkbox oben links (bei Hover oder selectMode sichtbar) — Plan 04
 *  1. Header: Vereinsname + Hotlead-Stern + Drag-Handle
 *  2. E-Mail-Zeile (klickbar, mailto:)
 *  3. Tag-Chips
 *  4. Meta-Zeile: "Letzter Kontakt vor X Tagen" + Notiz-Icon + Deal-Rotting-Badge
 *
 * Checkbox-Verhalten (ADMIN-03):
 *  - Sichtbar wenn: selectMode=true ODER Lead ist selektiert ODER Karte hover (group-hover)
 *  - Click: stopPropagation + toggleLeadSelection(lead.id)
 *  - Wenn selektiert: gelber Border (border-2 border-[#E9FF57])
 *
 * Shift-Klick auf Karte (nicht Checkbox): aktiviert selectMode + selektiert diese Karte.
 *
 * CI-Farben: #E9FF57 / #1A1A1A / #DAD9D9. Keine weisse Schrift auf gelbem Background.
 */

import { useDraggable } from '@dnd-kit/core';
import { GripVertical, Star, FileText, Bell, RefreshCw, PauseCircle } from 'lucide-react';
import type { Lead } from '../types';
import { dealRottingBadge } from '../lib/dealRotting';
import { lastContactLabel } from '../lib/lastContact';
import { taskBadgeState } from '../lib/taskBadgeState';
import { useLeadBoard } from '../store/useLeadBoard';

interface CardProps {
  lead: Lead;
  now: Date;
}

export default function Card({ lead, now }: CardProps) {
  const selectLead = useLeadBoard((s) => s.selectLead);
  const leadTasks = useLeadBoard((s) => s.tasksByLead[lead.id]);
  const selectedLeadIds = useLeadBoard((s) => s.selectedLeadIds);
  const selectMode = useLeadBoard((s) => s.selectMode);
  const toggleLeadSelection = useLeadBoard((s) => s.toggleLeadSelection);
  const toggleSelectMode = useLeadBoard((s) => s.toggleSelectMode);

  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `card-${lead.id}`,
    data: { leadId: lead.id, fromStatus: lead.status },
  });

  const style: React.CSSProperties = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        opacity: isDragging ? 0.6 : 1,
      }
    : { opacity: isDragging ? 0.6 : 1 };

  const isSelected = selectedLeadIds.has(lead.id);
  const isHotlead = lead.tags.includes('hotlead');
  const rotting = dealRottingBadge(lead, now);
  const hasNote = Boolean(lead.notes && lead.notes.trim().length > 0);
  const otherTags = lead.tags.filter((t) => t !== 'hotlead');
  const hasTaskToday = taskBadgeState(leadTasks, now);

  const handleCardClick = (e: React.MouseEvent) => {
    if (e.shiftKey) {
      // Shift-Klick aktiviert Select-Mode und selektiert diese Karte
      if (!selectMode) toggleSelectMode();
      toggleLeadSelection(lead.id);
    } else {
      // Normaler Klick oeffnet Modal (nur wenn kein selectMode aktiv)
      // Im selectMode toggelt Klick direkt die Selektion
      if (selectMode) {
        toggleLeadSelection(lead.id);
      } else {
        selectLead(lead.id);
      }
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={handleCardClick}
      className={
        'group relative bg-white rounded p-3 mb-2 cursor-pointer hover:shadow-md min-h-[120px] transition-shadow ' +
        (isSelected
          ? 'border-2 border-[#E9FF57]'
          : 'border border-[#DAD9D9]')
      }
    >
      {/* Checkbox oben links — sichtbar bei Hover (group-hover), selectMode oder selektiert */}
      <div className="absolute top-2 left-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => toggleLeadSelection(lead.id)}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Lead ${lead.vereinName} auswaehlen`}
          className={
            'w-4 h-4 cursor-pointer accent-[#E9FF57] ' +
            (isSelected || selectMode ? 'opacity-100' : 'opacity-0 group-hover:opacity-100')
          }
        />
      </div>

      {/* Header — mit Padding links fuer Checkbox */}
      <div className="flex items-center justify-between gap-2 pl-6">
        <div className="flex items-center gap-1 min-w-0">
          <span className="text-base font-semibold text-[#1A1A1A] truncate">
            {lead.vereinName}
          </span>
          {isHotlead && (
            <Star
              className="w-4 h-4 fill-[#E9FF57] text-[#E9FF57] ml-1 shrink-0"
              aria-label="Hotlead"
            />
          )}
          {lead.autoWorkflowActive ? (
            <span
              className="ml-1 shrink-0"
              title="Auto-Workflow aktiv — Karte wandert automatisch nach 7 Tagen, Follow-up-Mails werden versendet"
              aria-label="Auto-Workflow aktiv"
            >
              <RefreshCw className="w-3.5 h-3.5 text-green-600" />
            </span>
          ) : (
            <span
              className="ml-1 shrink-0"
              title="Auto-Workflow gestoppt — manuelle Bearbeitung, keine Auto-Mails"
              aria-label="Auto-Workflow gestoppt"
            >
              <PauseCircle className="w-3.5 h-3.5 text-gray-400" />
            </span>
          )}
        </div>
        {hasTaskToday && (
          <span
            className="inline-flex items-center gap-1 bg-[#E9FF57] text-[#1A1A1A] px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0"
            title="Task heute faellig oder ueberfaellig"
            aria-label="Task heute faellig"
          >
            <Bell className="w-3 h-3" />
            Heute
          </span>
        )}
        <button
          type="button"
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-[#1A1A1A] p-1"
          aria-label="Karte verschieben"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>

      {/* E-Mail */}
      {lead.vereinEmail && (
        <a
          href={`mailto:${lead.vereinEmail}`}
          onClick={(e) => e.stopPropagation()}
          className="text-sm text-gray-600 hover:underline hover:text-[#1A1A1A] block truncate mt-1 pl-6"
        >
          {lead.vereinEmail}
        </a>
      )}

      {/* Tag-Chips */}
      {otherTags.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2 pl-6">
          {otherTags.map((tag) => (
            <span
              key={tag}
              className="bg-gray-100 text-gray-700 px-2 py-0.5 text-xs rounded"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Meta-Zeile */}
      <div className="flex items-center justify-between mt-2 text-xs text-gray-500 gap-2 pl-6">
        <span className="flex items-center gap-1 min-w-0">
          <span className="truncate">Letzter Kontakt: {lastContactLabel(lead, now)}</span>
          {hasNote && (
            <FileText
              className="w-3 h-3 inline shrink-0 text-gray-400"
              aria-label="Notiz vorhanden"
            />
          )}
        </span>
        {rotting && (
          <span
            className={
              rotting.color === 'orange'
                ? 'bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs shrink-0'
                : 'bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs shrink-0'
            }
          >
            {rotting.label}
          </span>
        )}
      </div>
    </div>
  );
}
